import { FormValidationAlert } from '@pkg/form/forms/FormValidationAlert'
import { type AppFieldExtendedReactFormApi, createFormHook, createFormHookContexts } from '@tanstack/react-form'
import { type ComponentType, useContext } from 'react'
import { ArrayPush } from './fields/ArrayPush'
import { CaptchaField } from './fields/CaptchaField'
import { CascaderField } from './fields/CascaderField'
import { CheckboxField } from './fields/CheckboxField'
import { CheckboxGroupField } from './fields/CheckboxGroupField'
import { ColorPickerField } from './fields/ColorPickerField'
import { DateInputField } from './fields/DateInputField'
import { DateMonthRangePickerField } from './fields/DateMonthRangePickerField'
import { DatePickerField } from './fields/DatePickerField'
import { DateQuarterRangePickerField } from './fields/DateQuarterRangePickerField'
import { DateRangeField } from './fields/DateRangeField'
import { DateSearchInputField } from './fields/DateSearchInputField'
import { DateTimePickerField } from './fields/DateTimePickerField'
import { DateTimeRangePickerField } from './fields/DateTimeRangePickerField'
import { DateWeekRangePickerField } from './fields/DateWeekRangePickerField'
import { DateYearRangePickerField } from './fields/DateYearRangePickerField'
import { DigitField } from './fields/DigitField'
import { DigitRangeField } from './fields/DigitRangeField'
import { GroupField } from './fields/GroupField'
import { InputNumberField } from './fields/InputNumberField'
import { MoneyField } from './fields/MoneyField'
import { PasswordField } from './fields/PasswordField'
import { RadioField } from './fields/RadioField'
import { RadioGroupField } from './fields/RadioGroupField'
import { RateField } from './fields/RateField'
import { SegmentedField } from './fields/SegmentedField'
import { SelectField } from './fields/SelectField'
import { SignaturePadField } from './fields/SignaturePadField.js'
import { SliderField } from './fields/SliderField'
import { SwitchField } from './fields/SwitchField'
import { TextAreaField } from './fields/TextAreaField'
// import { AppModalForm } from './forms/AppModalForm'
// import { AppDrawerForm } from './forms/AppDrawerForm'
// import { AppStepsForm } from './forms/AppStepsForm'
import { TextField } from './fields/TextField'
import { TimePickerField } from './fields/TimePickerField'
import { TimeRangePickerField } from './fields/TimeRangePickerField'
import { TreeSelectField } from './fields/TreeSelectField'
import { UploadButtonField } from './fields/UploadButtonField'
import { UploadDraggerField } from './fields/UploadDraggerField'
import { AppDrawerForm } from './forms/AppDrawerForm'
import { AppProForm } from './forms/AppProForm'
import { SubmitButton } from './forms/SubmitButton'

// Create and export contexts/hooks
export const { fieldContext, formContext, useFieldContext } = createFormHookContexts()

const fieldComponents = {
  ArrayPush: ArrayPush,
  Captcha: CaptchaField,
  Cascader: CascaderField,
  Checkbox: CheckboxField,
  CheckboxGroup: CheckboxGroupField,
  ColorPicker: ColorPickerField,
  DateInput: DateInputField,
  DatePicker: DatePickerField,
  DateRangePicker: DateRangeField,
  DateSearchInput: DateSearchInputField,
  DateTimePicker: DateTimePickerField,
  DateTimeRangePicker: DateTimeRangePickerField,
  DateMonthRangePicker: DateMonthRangePickerField,
  DateQuarterRangePicker: DateQuarterRangePickerField,
  DateWeekRangePicker: DateWeekRangePickerField,
  DateYearRangePicker: DateYearRangePickerField,
  Digit: DigitField,
  DigitRange: DigitRangeField,
  Group: GroupField,
  InputNumber: InputNumberField,
  Money: MoneyField,
  Radio: RadioField,
  RadioGroup: RadioGroupField,
  Rate: RateField,
  Segmented: SegmentedField,
  Select: SelectField,
  SignaturePad: SignaturePadField,
  Slider: SliderField,
  Switch: SwitchField,
  Text: TextField,
  Password: PasswordField,
  TextArea: TextAreaField,
  TimePicker: TimePickerField,
  TimeRangePicker: TimeRangePickerField,
  TreeSelect: TreeSelectField,
  UploadButton: UploadButtonField,
  UploadDragger: UploadDraggerField,
} as const

const formComponents = {
  ArrayPush: ArrayPush,
  ProForm: AppProForm,
  // ModalForm: AppModalForm,
  DrawerForm: AppDrawerForm,
  // StepsForm: AppStepsForm,
  SubmitButton,
  ValidationAlert: FormValidationAlert,
} as const

export type AnyAppFieldExtendedReactFormApi<
  TForm = unknown,
  TFieldComponents extends Record<string, ComponentType<any>> = typeof fieldComponents,
  TFormComponents extends Record<string, ComponentType<any>> = typeof formComponents,
> = AppFieldExtendedReactFormApi<
  TForm,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  TFieldComponents,
  TFormComponents
>

export function useFormContext<
  TForm = unknown,
  TFieldComponents extends Record<string, ComponentType<any>> = typeof fieldComponents,
  TFormComponents extends Record<string, ComponentType<any>> = typeof formComponents,
>(): AnyAppFieldExtendedReactFormApi<TForm, TFieldComponents, TFormComponents> {
  // this is the same impl as the original from createFormHookContexts but we allow typing the form type
  const ctx = useContext(formContext)
  if (!ctx) {
    throw new Error('useFormContext must be used within form.AppForm or form.Provider')
  }
  return ctx as never
}

export function useOptionalFormContext<
  TForm = unknown,
  TFieldComponents extends Record<string, ComponentType<any>> = typeof fieldComponents,
  TFormComponents extends Record<string, ComponentType<any>> = typeof formComponents,
>(form?: unknown): AnyAppFieldExtendedReactFormApi<TForm, TFieldComponents, TFormComponents> {
  // this is the same impl as the original from createFormHookContexts but we allow typing the form type
  const ctx = useContext(formContext)
  if (!ctx && !form) {
    throw new Error(
      'useOptionalFormContext must be used within form.AppForm or form.Provider or form must be provided with opts',
    )
  }
  return (form ?? ctx) as never
}

// Base hook with component registries
export function createAppFormHook<
  TFieldComponents extends Record<string, ComponentType<any>> = typeof fieldComponents,
  TFormComponents extends Record<string, ComponentType<any>> = typeof formComponents,
>(options: { fieldComponents?: TFieldComponents; formComponents?: TFormComponents } = {}) {
  const useAppFormContext = useFormContext as <TForm = unknown>() => AnyAppFieldExtendedReactFormApi<
    TForm,
    typeof fieldComponents & TFieldComponents,
    typeof formComponents & TFormComponents
  >

  return {
    ...createFormHook<typeof fieldComponents & TFieldComponents, typeof formComponents & TFormComponents>({
      fieldContext,
      formContext,
      fieldComponents: { ...fieldComponents, ...options.fieldComponents } as never,
      formComponents: { ...formComponents, ...options.formComponents } as never,
    }),
    useAppFormContext,
  }
}
